import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { notify } from "../components/Toast";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { refreshOrgCatalog } from "../hooks/useOrgCatalog";
import TimeSelect from "../components/TimeSelect";
// Curated list of common IANA timezones — used in the Profile dropdown so users
// can't accidentally save an invalid string like "America/California".
const TIMEZONE_OPTIONS = [
    { label: "Eastern (America/New_York)", value: "America/New_York" },
    { label: "Central (America/Chicago)", value: "America/Chicago" },
    { label: "Mountain (America/Denver)", value: "America/Denver" },
    { label: "Pacific (America/Los_Angeles)", value: "America/Los_Angeles" },
    { label: "Alaska (America/Anchorage)", value: "America/Anchorage" },
    { label: "Hawaii (Pacific/Honolulu)", value: "Pacific/Honolulu" },
    { label: "UTC", value: "UTC" },
    { label: "London (Europe/London)", value: "Europe/London" },
    { label: "Paris (Europe/Paris)", value: "Europe/Paris" },
    { label: "Tokyo (Asia/Tokyo)", value: "Asia/Tokyo" },
    { label: "Sydney (Australia/Sydney)", value: "Australia/Sydney" },
];
// Return Monday (YYYY-MM-DD) of the ISO week containing `d`.
function isoMonday(d) {
    const day = d.getDay(); // 0=Sun..6=Sat — convert to Mon=0..Sun=6
    const monIndex = (day + 6) % 7;
    const m = new Date(d);
    m.setDate(d.getDate() - monIndex);
    m.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(m.getDate())}`;
}
function fmtMondayLabel(weekStart) {
    const [y, mo, d] = weekStart.split("-").map(Number);
    const start = new Date(y, mo - 1, d);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const opts = { month: "short", day: "numeric" };
    return `Week of ${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}
export default function SettingsPage() {
    const { user, refresh, logout } = useAuth();
    // Optional-chain through to .slice — `user?.awake_start.slice()` only
    // guards `user`, so when user is null on first render the `.slice`
    // call still throws and white-screens the page. The extra `?.` makes
    // the whole expression short-circuit to undefined → falls back to the
    // default. (Hooks run before the `if (!user) return null` guard, so
    // the guard can't save us here.)
    const [name, setName] = useState(user?.name ?? "");
    const [defaultStart, setDefaultStart] = useState(user?.awake_start?.slice(0, 5) ?? "07:00");
    const [defaultEnd, setDefaultEnd] = useState(user?.awake_end?.slice(0, 5) ?? "22:00");
    const [tz, setTz] = useState(user?.timezone ?? "UTC");
    const [msg, setMsg] = useState(null);
    // Live mirror of the default-hours pair. The two TimeSelect handlers
    // used to read the *other* field straight out of closure state
    // (`defaultEnd` in the start handler, `defaultStart` in the end one).
    // Closure state is captured at render time, so changing start then
    // end before the next render — or the two patches racing — could
    // PATCH the stale half and persist a window the user never chose.
    // Each handler now writes the changed field into this ref
    // synchronously and reads BOTH from the ref, so the save always sends
    // the true current pair regardless of render timing.
    const hoursRef = useRef({ start: defaultStart, end: defaultEnd });
    useEffect(() => {
        hoursRef.current = { start: defaultStart, end: defaultEnd };
    }, [defaultStart, defaultEnd]);
    if (!user)
        return null;
    // Single code path for persisting the default-hours pair — both
    // TimeSelect handlers funnel through here with the full {start, end}
    // so there's no stale-closure read and no inter-handler race.
    async function saveDefaultHours(nextStart, nextEnd) {
        try {
            await api.patch("/users/me", {
                awake_start: `${nextStart}:00`,
                awake_end: `${nextEnd}:00`,
            });
            // Best-effort: clear THIS week's per-week override so the new
            // default actually shows on today's calendar.
            try {
                await api.del(`/users/me/awake-schedules/${isoMonday(new Date())}`);
            }
            catch {
                /* no current-week override to clear */
            }
            await refresh();
            setMsg("Saved.");
            setTimeout(() => setMsg(null), 1500);
        }
        catch (e) {
            setMsg(e instanceof Error ? `Save failed: ${e.message}` : "Save failed");
        }
    }
    async function saveProfile() {
        // Wrap in try/catch so a failed save surfaces a visible message
        // instead of silently swallowing the error (which was masking the
        // earlier PATCH /users/me 500 and making the Save button look broken).
        try {
            await api.patch("/users/me", {
                name,
                awake_start: `${defaultStart}:00`,
                awake_end: `${defaultEnd}:00`,
                timezone: tz,
            });
            // Best-effort: clear THIS week's per-week override so the new
            // default actually shows up on the calendar. Without this, an
            // older override (set automatically by the inline editor or by
            // a previous Settings session) keeps masking the default and the
            // calendar appears to "lose" hours despite the save succeeding.
            // 404 is fine — it just means no override existed.
            try {
                const today = isoMonday(new Date());
                await api.del(`/users/me/awake-schedules/${today}`);
            }
            catch {
                /* no current-week override — nothing to clear */
            }
            await refresh();
            setMsg("Saved.");
            setTimeout(() => setMsg(null), 2000);
        }
        catch (e) {
            setMsg(e instanceof Error ? `Save failed: ${e.message}` : "Save failed");
        }
    }
    async function exportData() {
        const blob = await fetch("/api/gdpr/export", { credentials: "include" }).then((r) => r.blob());
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `timeblock-export-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    async function deleteAccount() {
        if (!confirm("Permanently delete your account and all your data? This cannot be undone.")) {
            return;
        }
        try {
            await api.del("/gdpr/account");
            await logout();
            window.location.href = "/login";
        }
        catch (e) {
            setMsg(e instanceof Error ? e.message : "Delete failed");
        }
    }
    return (_jsxs("div", { className: "page settings-page", children: [_jsx("h1", { children: "Settings" }), _jsxs("section", { children: [_jsx("h2", { children: "Profile" }), _jsxs("label", { children: ["Name", _jsx("input", { value: name, onChange: (e) => setName(e.target.value) })] }), _jsxs("label", { children: ["Timezone", _jsxs("select", { value: tz, onChange: (e) => setTz(e.target.value), children: [TIMEZONE_OPTIONS.map((tzOpt) => (_jsx("option", { value: tzOpt.value, children: tzOpt.label }, tzOpt.value))), !TIMEZONE_OPTIONS.some((o) => o.value === tz) && tz && (_jsxs("option", { value: tz, children: [tz, " (current)"] }))] }), _jsx("small", { className: "muted", children: "Used to interpret your awake hours, recurring meetings, and calendar day boundaries. If yours isn't listed, type it manually below." })] }), _jsxs("label", { children: ["Custom timezone (advanced \u2014 must be a valid IANA name like \"America/Los_Angeles\")", _jsx("input", { value: tz, onChange: (e) => setTz(e.target.value), placeholder: "e.g. America/Los_Angeles" })] }), _jsx("button", { onClick: saveProfile, className: "btn-primary", children: "Save profile" }), msg && _jsx("span", { className: "muted", style: { marginLeft: "0.75rem" }, children: msg })] }), _jsxs("section", { children: [_jsx("h2", { children: "Default awake hours" }), _jsx("p", { className: "muted", children: "The window your calendar grid renders by default. Override per week below if you have a non-standard schedule that week." }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Default start", _jsx(TimeSelect, { value: defaultStart, onChange: (v) => {
                                            hoursRef.current.start = v;
                                            setDefaultStart(v);
                                            void saveDefaultHours(hoursRef.current.start, hoursRef.current.end);
                                        } })] }), _jsxs("label", { children: ["Default end", _jsx(TimeSelect, { value: defaultEnd, onChange: (v) => {
                                            hoursRef.current.end = v;
                                            setDefaultEnd(v);
                                            void saveDefaultHours(hoursRef.current.start, hoursRef.current.end);
                                        } })] })] }), _jsx("button", { onClick: saveProfile, className: "btn-primary", children: "Save defaults" })] }), _jsx(AwakeSchedulesSection, { defaultStart: defaultStart, defaultEnd: defaultEnd }), _jsx(NotificationPreferencesSection, {}), _jsx(CalendarSyncSection, {}), _jsx(ChangePasswordSection, {}), user.role === "admin" && _jsx(AdminOrgsSection, {}), _jsxs("section", { children: [_jsx("h2", { children: "Data & privacy (GDPR)" }), _jsx("p", { className: "muted", children: "You can download a complete copy of your account data, or permanently delete your account and all associated time blocks, recurring patterns, notifications, and assignments." }), _jsxs("div", { className: "row", children: [_jsx("button", { onClick: exportData, children: "Export my data" }), _jsx("button", { onClick: deleteAccount, className: "btn-danger", children: "Delete account" })] })] })] }));
}
// A small pill-style on/off switch used by the notification prefs rows.
function PrefToggle({ on, onChange, label, }) {
    return (_jsx("button", { type: "button", className: `pref-toggle ${on ? "on" : ""}`, onClick: onChange, "aria-pressed": on, "aria-label": label, children: _jsx("span", { className: "pref-toggle-knob", "aria-hidden": "true" }) }));
}
// Notification preferences — per-user toggles for the in-app reminders.
// Reminder content is generated when the app is opened (no background
// worker), which the copy notes. Each change auto-saves via PATCH.
function NotificationPreferencesSection() {
    const [prefs, setPrefs] = useState(null);
    useEffect(() => {
        api
            .get("/notifications/preferences")
            .then(setPrefs)
            .catch(() => setPrefs(null));
    }, []);
    async function patch(changes) {
        if (!prefs)
            return;
        setPrefs({ ...prefs, ...changes }); // optimistic
        try {
            const saved = await api.patch("/notifications/preferences", changes);
            setPrefs(saved);
        }
        catch (e) {
            // Revert by refetching the server's truth.
            try {
                setPrefs(await api.get("/notifications/preferences"));
            }
            catch {
                /* leave whatever we had */
            }
            notify.error(e instanceof Error ? e.message : "Couldn't save preference");
        }
    }
    if (!prefs)
        return null;
    return (_jsxs("section", { children: [_jsx("h2", { children: "Notification preferences" }), _jsx("p", { className: "muted", children: "Choose which in-app reminders you receive. Reminders are generated when you open the app." }), _jsxs("div", { className: "pref-list", children: [_jsxs("div", { className: "pref-row", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "Block-start reminders" }), _jsx("span", { className: "muted small", children: "A nudge before a block begins" })] }), _jsxs("select", { value: prefs.block_start_lead_minutes, disabled: !prefs.block_start_reminders, onChange: (e) => patch({
                                    block_start_lead_minutes: Number(e.target.value),
                                }), children: [_jsx("option", { value: 5, children: "5 min before" }), _jsx("option", { value: 10, children: "10 min before" }), _jsx("option", { value: 15, children: "15 min before" }), _jsx("option", { value: 30, children: "30 min before" })] }), _jsx(PrefToggle, { label: "Block-start reminders", on: prefs.block_start_reminders, onChange: () => patch({
                                    block_start_reminders: !prefs.block_start_reminders,
                                }) })] }), _jsxs("div", { className: "pref-row", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "Morning planning nudge" }), _jsx("span", { className: "muted small", children: "When your day has unscheduled time" })] }), _jsx(PrefToggle, { label: "Morning planning nudge", on: prefs.morning_planning_nudge, onChange: () => patch({
                                    morning_planning_nudge: !prefs.morning_planning_nudge,
                                }) })] }), _jsxs("div", { className: "pref-row", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "End-of-day review" }), _jsx("span", { className: "muted small", children: "Prompt to log progress on past blocks" })] }), _jsx(PrefToggle, { label: "End-of-day review", on: prefs.end_of_day_review, onChange: () => patch({ end_of_day_review: !prefs.end_of_day_review }) })] }), _jsxs("div", { className: "pref-row", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "Task assigned to me" }), _jsx("span", { className: "muted small", children: "When someone assigns you work" })] }), _jsx(PrefToggle, { label: "Task assigned to me", on: prefs.task_assigned, onChange: () => patch({ task_assigned: !prefs.task_assigned }) })] }), _jsxs("div", { className: "pref-row", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "Due-date warnings" }), _jsx("span", { className: "muted small", children: "When an assigned task is due soon" })] }), _jsx(PrefToggle, { label: "Due-date warnings", on: prefs.due_date_warnings, onChange: () => patch({ due_date_warnings: !prefs.due_date_warnings }) })] })] }), _jsxs("div", { className: "pref-row pref-row-digest", children: [_jsxs("div", { className: "pref-row-text", children: [_jsx("strong", { children: "Daily email digest" }), _jsx("span", { className: "muted small", children: "A morning summary if you haven't opened the app \u00B7 email sending is coming soon" })] }), _jsxs("select", { value: prefs.email_digest ? prefs.email_digest_hour : "off", onChange: (e) => {
                            const v = e.target.value;
                            if (v === "off")
                                patch({ email_digest: false });
                            else
                                patch({
                                    email_digest: true,
                                    email_digest_hour: Number(v),
                                });
                        }, children: [_jsx("option", { value: "off", children: "Off" }), _jsx("option", { value: 6, children: "6:00 am" }), _jsx("option", { value: 7, children: "7:00 am" }), _jsx("option", { value: 8, children: "8:00 am" }), _jsx("option", { value: 9, children: "9:00 am" })] })] })] }));
}
// Connected calendars — preview shell. Calendar sync (Outlook / Google
// OAuth + two-way sync) needs OAuth app credentials registered before it
// can do anything, so the Connect buttons are intentionally inert. This
// section exists so the design is visible and reviewable now; the real
// connect flow lands once the OAuth apps are set up.
function CalendarSyncSection() {
    return (_jsxs("section", { children: [_jsxs("div", { className: "settings-section-head", children: [_jsx("h2", { children: "Connected calendars" }), _jsx("span", { className: "pill", children: "Coming soon" })] }), _jsx("p", { className: "muted", children: "Pull external meetings in so your day is accurate, and optionally push your blocks out. This is a preview \u2014 connecting a calendar isn't available yet." }), _jsxs("div", { className: "cal-sync-card", children: [_jsx("span", { className: "cal-sync-icon", "aria-hidden": "true", children: "\uD83E\uDE9F" }), _jsxs("div", { className: "cal-sync-titleblock", children: [_jsx("strong", { children: "Outlook \u2014 Microsoft 365" }), _jsx("span", { className: "muted small", children: "Not connected" })] }), _jsx("button", { className: "btn-primary", disabled: true, title: "Calendar sync is coming soon", children: "Connect" })] }), _jsxs("div", { className: "cal-sync-card", children: [_jsx("span", { className: "cal-sync-icon", "aria-hidden": "true", children: "\uD83D\uDCC5" }), _jsxs("div", { className: "cal-sync-titleblock", children: [_jsx("strong", { children: "Google Calendar" }), _jsx("span", { className: "muted small", children: "Not connected" })] }), _jsx("button", { className: "btn-primary", disabled: true, title: "Calendar sync is coming soon", children: "Connect" })] }), _jsx("p", { className: "muted small cal-sync-foot", children: "When this ships: imported meetings show on your calendar as blocks, and your blocks can optionally push back to Outlook or Google." })] }));
}
function AwakeSchedulesSection({ defaultStart, defaultEnd, }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    async function load() {
        setLoading(true);
        const today = isoMonday(new Date());
        const list = await api.get(`/users/me/awake-schedules?from_week=${today}`);
        setSchedules(list);
        setLoading(false);
    }
    useEffect(() => {
        load();
    }, []);
    async function upsert(weekStart, awakeStart, awakeEnd) {
        await api.put("/users/me/awake-schedules", {
            week_start: weekStart,
            awake_start: `${awakeStart}:00`,
            awake_end: `${awakeEnd}:00`,
        });
        await load();
    }
    async function remove(weekStart) {
        if (!confirm("Remove this week override and fall back to your defaults?"))
            return;
        await api.del(`/users/me/awake-schedules/${weekStart}`);
        await load();
    }
    function quickAdd(weeksAhead) {
        const d = new Date();
        d.setDate(d.getDate() + weeksAhead * 7);
        const week = isoMonday(d);
        upsert(week, defaultStart, defaultEnd);
    }
    const today = isoMonday(new Date());
    const nextWeek = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return isoMonday(d);
    })();
    return (_jsxs("section", { children: [_jsx("h2", { children: "Per-week awake hours" }), _jsx("p", { className: "muted", children: "Set different awake hours for specific weeks (e.g., conference week, vacation, an early-shift sprint). Weeks without an override use your defaults above." }), _jsxs("div", { className: "row", style: { marginBottom: "1rem" }, children: [_jsx("button", { onClick: () => quickAdd(0), children: "+ Add for this week" }), _jsx("button", { onClick: () => quickAdd(1), children: "+ Add for next week" })] }), loading ? (_jsx("div", { className: "muted", children: "Loading\u2026" })) : schedules.length === 0 ? (_jsx("div", { className: "muted", children: "No per-week overrides yet." })) : (_jsx("ul", { className: "schedule-list", children: schedules.map((s) => (_jsx(ScheduleRow, { schedule: s, isCurrentWeek: s.week_start === today, isNextWeek: s.week_start === nextWeek, onChange: upsert, onDelete: remove }, s.id))) })), _jsx(CustomWeekAdd, { onAdd: (week) => upsert(week, defaultStart, defaultEnd) })] }));
}
function ScheduleRow({ schedule, isCurrentWeek, isNextWeek, onChange, onDelete, }) {
    const [start, setStart] = useState(schedule.awake_start.slice(0, 5));
    const [end, setEnd] = useState(schedule.awake_end.slice(0, 5));
    // Save expects fresh values — closure-captured `start`/`end` would lag one
    // setState behind when fired from TimeSelect's onChange. The dropdown handlers
    // below pass the new value explicitly so this stays correct.
    async function saveWith(nextStart, nextEnd) {
        if (nextStart === schedule.awake_start.slice(0, 5) &&
            nextEnd === schedule.awake_end.slice(0, 5)) {
            return;
        }
        await onChange(schedule.week_start, nextStart, nextEnd);
    }
    return (_jsxs("li", { className: "schedule-row", children: [_jsxs("div", { className: "schedule-week", children: [_jsx("strong", { children: fmtMondayLabel(schedule.week_start) }), isCurrentWeek && _jsx("span", { className: "pill ok", children: "This week" }), isNextWeek && _jsx("span", { className: "pill ok", children: "Next week" })] }), _jsx(TimeSelect, { value: start, onChange: (v) => {
                    setStart(v);
                    saveWith(v, end);
                } }), _jsx("span", { className: "muted", children: "\u2192" }), _jsx(TimeSelect, { value: end, onChange: (v) => {
                    setEnd(v);
                    saveWith(start, v);
                } }), _jsx("button", { onClick: () => onDelete(schedule.week_start), className: "btn-link", children: "Remove" })] }));
}
function CustomWeekAdd({ onAdd }) {
    const [date, setDate] = useState("");
    return (_jsxs("div", { className: "row", style: { marginTop: "1rem", alignItems: "end" }, children: [_jsxs("label", { children: ["Add for any date (we'll snap to the Monday of that week)", _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value) })] }), _jsx("button", { disabled: !date, onClick: () => {
                    if (!date)
                        return;
                    const [y, m, d] = date.split("-").map(Number);
                    onAdd(isoMonday(new Date(y, m - 1, d)));
                    setDate("");
                }, children: "Add week" })] }));
}
// ---------- Change my own password ----------
function ChangePasswordSection() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [okMsg, setOkMsg] = useState(null);
    async function submit(e) {
        e.preventDefault();
        setError(null);
        setOkMsg(null);
        if (next.length < 10) {
            setError("New password must be at least 10 characters.");
            return;
        }
        if (next !== confirm) {
            setError("New passwords don't match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            setOkMsg("Password updated.");
            setCurrent("");
            setNext("");
            setConfirm("");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Couldn't change password.");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("section", { children: [_jsx("h2", { children: "Password" }), _jsx("p", { className: "muted small", children: "Change your password. You'll stay signed in on this device." }), _jsxs("form", { className: "create-user-form", onSubmit: submit, children: [_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Current password", _jsx("input", { type: "password", value: current, onChange: (e) => setCurrent(e.target.value), autoComplete: "current-password" })] }), _jsxs("label", { children: ["New password", _jsx("input", { type: "password", value: next, onChange: (e) => setNext(e.target.value), autoComplete: "new-password", placeholder: "\u2265 10 characters" })] }), _jsxs("label", { children: ["Confirm new password", _jsx("input", { type: "password", value: confirm, onChange: (e) => setConfirm(e.target.value), autoComplete: "new-password" })] })] }), error && _jsx("div", { className: "error small", children: error }), okMsg && _jsx("div", { className: "create-user-ok small", children: okMsg }), _jsx("button", { className: "btn-primary", type: "submit", disabled: busy, children: busy ? "Updating…" : "Change password" })] })] }));
}
// ---------- Admin: create a new user ----------
// Account creation is admin-only (there's no public signup). This posts
// straight to /auth/signup — NOT through useAuth().signup, which would swap
// the admin's own session to the new account. The backend keeps the admin's
// cookie and just returns the created user; we refresh the team list.
function CreateUserForm({ onCreated }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [okMsg, setOkMsg] = useState(null);
    async function submit(e) {
        e.preventDefault();
        setError(null);
        setOkMsg(null);
        if (!name.trim() || !email.trim()) {
            setError("Name and email are required.");
            return;
        }
        if (password.length < 10) {
            setError("Password must be at least 10 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/signup", {
                name: name.trim(),
                email: email.trim().toLowerCase(),
                password,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
                // Admin is creating on the user's behalf; consent is recorded so the
                // account is GDPR-clean. The new user can review/withdraw in Settings.
                gdpr_consent: true,
            });
            setOkMsg(`Created ${name.trim()} — they can sign in with the password you set.`);
            setName("");
            setEmail("");
            setPassword("");
            await onCreated();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Couldn't create the user.");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("form", { className: "create-user-form", onSubmit: submit, children: [_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Name", _jsx("input", { value: name, onChange: (e) => setName(e.target.value), placeholder: "Jane Doe" })] }), _jsxs("label", { children: ["Email", _jsx("input", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: "jane@company.com" })] }), _jsxs("label", { children: ["Temporary password", _jsx("input", { type: "text", value: password, onChange: (e) => setPassword(e.target.value), placeholder: "\u2265 10 characters" })] })] }), error && _jsx("div", { className: "error small", children: error }), okMsg && _jsx("div", { className: "create-user-ok small", children: okMsg }), _jsx("button", { className: "btn-primary", type: "submit", disabled: busy, children: busy ? "Creating…" : "Create user" })] }));
}
// ---------- Admin: business units, activities, user role + unit ----------
function AdminOrgsSection() {
    // The calling admin's own user id — we use it to hide deactivate /
    // delete controls on their own row so they can't lock themselves out
    // (the backend also refuses self-targeting, but rendering the buttons
    // there would invite confused 400s).
    const { user: currentUser } = useAuth();
    const [units, setUnits] = useState([]);
    const [activities, setActivities] = useState([]);
    const [users, setUsers] = useState([]);
    async function reload() {
        const [u, a, m] = await Promise.all([
            api.get("/orgs/business-units"),
            api.get("/orgs/activities"),
            api.get("/users"),
        ]);
        setUnits(u);
        setActivities(a);
        setUsers(m);
    }
    useEffect(() => {
        reload();
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs("section", { children: [_jsx("h2", { children: "Business units" }), _jsx("p", { className: "muted small", children: "Top-level groupings (Marketing, Legal, Sales, Tech, \u2026). A manager can create/edit projects within their assigned unit." }), _jsx(NamedList, { items: units, itemLabel: "business unit", onCreate: async (name) => {
                            await api.post("/orgs/business-units", { name });
                            refreshOrgCatalog();
                            reload();
                        }, onDelete: async (id) => {
                            if (!confirm("Delete this business unit? Projects in it will lose their tag."))
                                return;
                            await api.del(`/orgs/business-units/${id}`);
                            refreshOrgCatalog();
                            reload();
                        }, placeholder: "e.g. Marketing" })] }), _jsxs("section", { children: [_jsx("h2", { children: "Activities" }), _jsx("p", { className: "muted small", children: "Cross-cutting categories (OPS, Tech Dev, Sales, Biz Dev, \u2026). Used to tag projects independently of the business unit." }), _jsx(NamedList, { items: activities, itemLabel: "activity", onCreate: async (name) => {
                            await api.post("/orgs/activities", { name });
                            refreshOrgCatalog();
                            reload();
                        }, onDelete: async (id) => {
                            if (!confirm("Delete this activity? Projects tagged with it will lose the tag."))
                                return;
                            await api.del(`/orgs/activities/${id}`);
                            refreshOrgCatalog();
                            reload();
                        }, placeholder: "e.g. OPS" })] }), _jsxs("section", { children: [_jsx("h2", { children: "Add a teammate" }), _jsxs("p", { className: "muted small", children: ["Create a new account. They start as a ", _jsx("b", { children: "worker" }), " \u2014 set their role and business unit in the table below after they're created. Share the password with them; they sign in themselves (your session stays put)."] }), _jsx(CreateUserForm, { onCreated: reload })] }), _jsxs("section", { children: [_jsx("h2", { children: "Team \u2014 role & business unit" }), _jsx("p", { className: "muted small", children: "Assign roles and put each user in a business unit. Workers can claim and schedule tasks; managers can additionally create projects/tasks for their unit; admin (you) is cross-unit." }), _jsxs("table", { className: "user-admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Role" }), _jsx("th", { children: "Business unit" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: users.map((u) => (_jsx(UserAdminRow, { user: u, units: units, currentUserId: currentUser?.id, onChanged: reload }, u.id))) })] })] })] }));
}
function NamedList({ items, onCreate, onDelete, placeholder, itemLabel, }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault();
        if (busy)
            return;
        const trimmed = name.trim();
        if (!trimmed) {
            notify.error(`Please enter a ${itemLabel} name first.`);
            return;
        }
        setBusy(true);
        try {
            await onCreate(trimmed);
            setName("");
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Add failed");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("ul", { className: "named-list named-list-chips", children: [items.map((it) => (_jsxs("li", { className: "named-chip", children: [_jsx("span", { className: "named-chip-label", children: it.name }), _jsx("button", { type: "button", className: "named-chip-x", onClick: () => onDelete(it.id), title: `Remove ${itemLabel}`, "aria-label": `Remove ${it.name}`, children: "\u2715" })] }, it.id))), items.length === 0 && (_jsxs("li", { className: "muted small", children: ["No ", itemLabel, "s yet \u2014 add your first one below."] }))] }), _jsxs("form", { className: "add-named-form", onSubmit: submit, children: [_jsxs("label", { className: "add-named-label", children: ["Add a new ", itemLabel] }), _jsxs("div", { className: "row", children: [_jsx("input", { value: name, placeholder: placeholder, onChange: (e) => setName(e.target.value) }), _jsx("button", { type: "submit", className: "btn-primary", disabled: busy, children: busy ? "Adding…" : `+ Add ${itemLabel}` })] })] })] }));
}
function UserAdminRow({ user, units, currentUserId, onChanged, }) {
    // Two flags drive the lifecycle controls:
    //   - isSelf hides everything (admin can't manage their own row)
    //   - isOtherAdmin hides Deactivate/Delete but keeps the role dropdown,
    //     so an admin can demote another admin and *then* delete them.
    // Backend re-enforces both rules in _guard_admin_targeting_user.
    const isSelf = currentUserId !== undefined && user.id === currentUserId;
    const isOtherAdmin = user.role === "admin" && !isSelf;
    const canManageLifecycle = !isSelf && !isOtherAdmin;
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(null);
    async function setRole(role) {
        try {
            await api.patch(`/users/${user.id}`, { role });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Update failed");
        }
    }
    async function setPrimaryUnit(business_unit_id) {
        try {
            // When setting primary, ensure it's also in the membership set.
            const memberships = new Set(user.business_unit_ids);
            if (business_unit_id)
                memberships.add(business_unit_id);
            await api.patch(`/users/${user.id}`, {
                business_unit_id,
                business_unit_ids: Array.from(memberships),
            });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Update failed");
        }
    }
    async function toggleUnit(unitId) {
        const next = new Set(user.business_unit_ids);
        if (next.has(unitId)) {
            next.delete(unitId);
        }
        else {
            next.add(unitId);
        }
        // Don't allow removing the primary unit; if it's the only one, force-keep it.
        let primary = user.business_unit_id;
        if (primary && !next.has(primary)) {
            // user explicitly removed the primary unit — clear the primary too
            primary = null;
        }
        try {
            await api.patch(`/users/${user.id}`, {
                business_unit_id: primary,
                business_unit_ids: Array.from(next),
            });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Update failed");
        }
    }
    async function setBuOwner(unitId, makeOwner) {
        // Mark/unmark this user as owner of a specific BU. The user must
        // already be a member of the unit (toggleUnit adds membership); the
        // backend 400s otherwise. Owner power only takes effect while the
        // user's role is "owner" (auth.bu_owner_ids gates on role).
        try {
            await api.patch(`/users/${user.id}/bu-owner`, {
                business_unit_id: unitId,
                is_owner: makeOwner,
            });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update owner status");
        }
    }
    async function setBuToday(unitId, showOnToday) {
        // Toggle whether this BU appears in the user's Today "Your business
        // units today" widget. Separate from membership/ownership — the user
        // keeps full BU functionality in Projects either way.
        try {
            await api.patch(`/users/${user.id}/bu-today`, {
                business_unit_id: unitId,
                show_on_today: showOnToday,
            });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update Today visibility");
        }
    }
    async function setBuAccess(unitId, canEdit) {
        // Set this membership to Read (view-only) or Edit (can modify the
        // unit's projects/tasks). Restrict-only on the backend: only narrows a
        // manager's/owner's power — never elevates a worker, and owners keep
        // edit in units they own regardless (see auth.has_bu_power).
        try {
            await api.patch(`/users/${user.id}/bu-access`, {
                business_unit_id: unitId,
                can_edit: canEdit,
            });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update Read/Edit access");
        }
    }
    async function deactivate() {
        if (!confirm(`Deactivate ${user.name}? They'll be logged out immediately and won't be able to sign back in until you reactivate them.`)) {
            return;
        }
        setBusy("deactivate");
        try {
            await api.post(`/users/${user.id}/deactivate`);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Deactivate failed");
        }
        finally {
            setBusy(null);
        }
    }
    async function reactivate() {
        setBusy("reactivate");
        try {
            await api.post(`/users/${user.id}/reactivate`);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Reactivate failed");
        }
        finally {
            setBusy(null);
        }
    }
    async function resetPassword() {
        const pwd = window.prompt(`Set a new password for ${user.name} (at least 10 characters). They'll sign in with it; your session is unaffected.`);
        if (pwd === null)
            return; // cancelled
        if (pwd.length < 10) {
            notify.error("Password must be at least 10 characters.");
            return;
        }
        setBusy("reset");
        try {
            await api.post(`/users/${user.id}/reset-password`, { new_password: pwd });
            notify.error(`Password reset for ${user.name}.`);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Reset failed");
        }
        finally {
            setBusy(null);
        }
    }
    async function hardDelete() {
        setBusy("delete");
        try {
            await api.del(`/users/${user.id}`);
            setConfirmDelete(false);
            onChanged();
        }
        catch (e) {
            // The 409 from the backend includes a "reassign their owned
            // projects first" message — surface it verbatim so the admin
            // knows exactly what to do.
            notify.error(e instanceof Error ? e.message : "Delete failed");
        }
        finally {
            setBusy(null);
        }
    }
    return (_jsxs("tr", { className: user.is_active ? "" : "user-row-disabled", children: [_jsxs("td", { children: [_jsx("div", { children: user.name }), _jsx("div", { className: "muted small", children: user.email })] }), _jsxs("td", { children: [_jsxs("select", { value: user.role, onChange: (e) => setRole(e.target.value), children: [_jsx("option", { value: "admin", children: "Admin" }), _jsx("option", { value: "owner", children: "Owner" }), _jsx("option", { value: "manager", children: "Manager" }), _jsx("option", { value: "worker", children: "Worker" })] }), user.role === "owner" && user.owner_business_unit_ids.length === 0 && (_jsxs("div", { className: "muted small owner-hint", children: ["Mark which units they own with the ", _jsx("strong", { children: "Owner" }), " button in the Business units column \u2192"] }))] }), _jsxs("td", { children: [_jsxs("details", { className: "unit-multi-select", children: [_jsx("summary", { children: user.business_unit_ids.length === 0
                                    ? "— none —"
                                    : user.business_unit_ids.length === 1
                                        ? units.find((u) => u.id === user.business_unit_ids[0])?.name ?? "1 unit"
                                        : `${user.business_unit_ids.length} units` }), _jsxs("div", { className: "unit-multi-list", children: [_jsxs("p", { className: "muted small", children: ["Check every unit this user belongs to. \u2605 marks their primary unit (the default when they create projects). For each unit choose", " ", _jsx("strong", { children: "Read" }), " (view only) or ", _jsx("strong", { children: "Edit" }), " (can change that unit's projects & tasks).", user.role === "owner" && (_jsxs(_Fragment, { children: [" Mark a unit ", _jsx("strong", { children: "Owner" }), " to put this owner in charge of it \u2014 owners always keep Edit in units they own."] })), user.role === "worker" && (_jsx(_Fragment, { children: " Workers are view-only everywhere; promote them to Manager to grant Edit." })), user.role === "admin" && (_jsx(_Fragment, { children: " Admins have full access to every unit regardless of these settings." }))] }), units.map((u) => {
                                        const checked = user.business_unit_ids.includes(u.id);
                                        const isPrimary = user.business_unit_id === u.id;
                                        const isBuOwner = user.owner_business_unit_ids.includes(u.id);
                                        const canEdit = user.edit_business_unit_ids.includes(u.id);
                                        return (_jsxs("label", { className: "unit-multi-row", children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggleUnit(u.id) }), _jsx("span", { className: "unit-multi-name", children: u.name }), checked && (_jsx("button", { type: "button", className: `primary-star ${isPrimary ? "active" : ""}`, title: isPrimary ? "Primary unit" : "Make primary", onClick: (e) => {
                                                        e.preventDefault();
                                                        setPrimaryUnit(u.id);
                                                    }, children: "\u2605" })), checked && user.role === "owner" && (_jsx("button", { type: "button", className: `bu-owner-toggle ${isBuOwner ? "active" : ""}`, title: isBuOwner
                                                        ? "Owner of this unit — click to remove"
                                                        : "Make this user the owner of this unit", onClick: (e) => {
                                                        e.preventDefault();
                                                        setBuOwner(u.id, !isBuOwner);
                                                    }, children: "Owner" })), checked &&
                                                    (user.role === "manager" || user.role === "owner") &&
                                                    (isBuOwner ? (_jsx("span", { className: "access-locked", title: "Owners always have Edit access in units they own", children: "Edit" })) : (_jsxs("span", { className: "access-seg", role: "group", "aria-label": "Access level", children: [_jsx("button", { type: "button", className: !canEdit ? "active" : "", onClick: (e) => {
                                                                    e.preventDefault();
                                                                    setBuAccess(u.id, false);
                                                                }, children: "Read" }), _jsx("button", { type: "button", className: canEdit ? "active" : "", onClick: (e) => {
                                                                    e.preventDefault();
                                                                    setBuAccess(u.id, true);
                                                                }, children: "Edit" })] }))), checked && user.role === "worker" && (_jsx("span", { className: "access-locked muted", title: "Workers are view-only", children: "Read" })), checked && user.role === "admin" && (_jsx("span", { className: "access-locked muted", title: "Admins have full access everywhere", children: "Full" }))] }, u.id));
                                    })] })] }), user.business_unit_ids.length > 0 && (() => {
                        const memberUnits = units.filter((u) => user.business_unit_ids.includes(u.id));
                        const todayCount = memberUnits.filter((u) => user.today_business_unit_ids.includes(u.id)).length;
                        return (_jsxs("details", { className: "unit-multi-select today-select", children: [_jsxs("summary", { children: ["Today: ", todayCount, " of ", memberUnits.length] }), _jsxs("div", { className: "unit-multi-list", children: [_jsx("p", { className: "muted small", children: "Pick which units show in this user's Today \u201CYour business units\u201D widget. Hiding one keeps their full access in Projects." }), memberUnits.map((u) => {
                                            const showsOnToday = user.today_business_unit_ids.includes(u.id);
                                            return (_jsxs("label", { className: "unit-multi-row", children: [_jsx("input", { type: "checkbox", checked: showsOnToday, onChange: () => setBuToday(u.id, !showsOnToday) }), _jsx("span", { className: "unit-multi-name", children: u.name })] }, u.id));
                                        })] })] }));
                    })()] }), _jsx("td", { children: _jsx("span", { className: `status-pill ${user.is_active ? "status-active" : "status-disabled"}`, children: user.is_active ? "Active" : "Disabled" }) }), _jsxs("td", { children: [isSelf ? (_jsx("span", { className: "muted small", children: "\u2014 you \u2014" })) : isOtherAdmin ? (
                    // Demote first via the Role dropdown to unlock these actions.
                    _jsx("span", { className: "muted small", children: "Demote first" })) : (_jsxs("div", { className: "user-row-actions", children: [user.is_active ? (_jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: deactivate, disabled: busy !== null, title: "Sign them out and block future logins. Reversible.", children: busy === "deactivate" ? "…" : "Deactivate" })) : (_jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: reactivate, disabled: busy !== null, title: "Restore login access.", children: busy === "reactivate" ? "…" : "Reactivate" })), _jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: resetPassword, disabled: busy !== null, title: "Set a new password for this user (e.g. they forgot theirs).", children: busy === "reset" ? "…" : "Reset password" }), _jsx("button", { type: "button", className: "btn-danger btn-small", onClick: () => setConfirmDelete(true), disabled: busy !== null, title: "Permanently delete this user and their data. Irreversible.", children: "Delete\u2026" })] })), canManageLifecycle && confirmDelete && (_jsx(DeleteUserConfirmModal, { user: user, busy: busy === "delete", onCancel: () => setConfirmDelete(false), onConfirm: hardDelete }))] })] }));
}
// Typed-confirmation modal for hard-delete. Forces the admin to type the
// target's email exactly — protects against fat-finger clicks on the wrong
// row and against accidental Enter-key submits. The actual destructive
// action only fires after the confirmation button is clicked while the
// typed text matches.
function DeleteUserConfirmModal({ user, busy, onCancel, onConfirm, }) {
    const [typed, setTyped] = useState("");
    const matches = typed.trim().toLowerCase() === user.email.toLowerCase();
    return (_jsx("div", { className: "modal-backdrop", role: "dialog", "aria-modal": "true", onClick: busy ? undefined : onCancel, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("h2", { children: ["Permanently delete ", user.name, "?"] }), _jsxs("p", { className: "muted small", children: ["This ", _jsx("strong", { children: "cannot be undone." }), " All of their time blocks, recurring patterns, notifications, and task assignments will be deleted. Checklist items they were assigned to will be unassigned but kept. If they own any active projects, the delete will be refused \u2014 transfer ownership first."] }), _jsxs("p", { className: "muted small", children: ["To confirm, type the user's email address: ", _jsx("code", { children: user.email })] }), _jsx("input", { type: "text", autoFocus: true, value: typed, onChange: (e) => setTyped(e.target.value), placeholder: user.email, disabled: busy }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", className: "btn-secondary", onClick: onCancel, disabled: busy, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-danger", onClick: onConfirm, disabled: !matches || busy, children: busy ? "Deleting…" : "Permanently delete" })] })] }) }));
}
